'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useLocale } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

type Partner = {
  id: string;
  name: string;
  href: string;
  role: string;
  logoSrc: string | null;
  logoWidth: number;
  logoHeight: number;
};

export default function SponsorsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const locale = useLocale();
  const copy = getPublicStrings(locale).home.sponsors;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const portwiseUrl = process.env.NEXT_PUBLIC_PORTWISE_URL?.trim();
  const portwiseLabel = process.env.NEXT_PUBLIC_PORTWISE_LABEL?.trim() || copy.fallbackPartnerRole;
  const portwiseName = process.env.NEXT_PUBLIC_PORTWISE_NAME?.trim() || 'Portwise';
  const portwiseLogo = process.env.NEXT_PUBLIC_PORTWISE_LOGO_URL?.trim() || null;

  const partners: Partner[] = [
    {
      id: 'squashpoint',
      name: 'Squashpoint',
      href: 'https://www.squashpoint.com/',
      role: copy.squashpointRole,
      logoSrc: null,
      logoWidth: 200,
      logoHeight: 48,
    },
  ];

  if (portwiseUrl) {
    partners.push({
      id: 'portwise',
      name: portwiseName,
      href: portwiseUrl,
      role: portwiseLabel,
      logoSrc: portwiseLogo,
      logoWidth: 180,
      logoHeight: 48,
    });
  }

  return (
    <section
      id="sponsors"
      ref={sectionRef}
      className="border-b border-border bg-white px-6 py-14 md:px-12 md:py-20"
    >
      <div className="mx-auto max-w-7xl">
        <div
          className={`mx-auto mb-10 max-w-3xl text-center transition-all duration-700 md:mb-12 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gold">
            {copy.eyebrow}
          </span>
          <h2 className="mt-4 font-heading text-3xl font-bold text-foreground md:text-5xl">
            {copy.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground/70">{copy.description}</p>
        </div>

        <div
          className={`flex flex-col items-stretch justify-center gap-8 md:flex-row md:items-center md:gap-12 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          {partners.map((p) => (
            <Link
              key={p.id}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-1 flex-col items-center justify-center rounded-2xl border border-border bg-background/80 px-8 py-10 grayscale transition-all hover:scale-[1.01] hover:grayscale-0 hover:shadow-lg"
            >
              {p.logoSrc ? (
                p.logoSrc.startsWith('http') ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={p.logoSrc}
                    alt={p.name}
                    className="h-10 w-auto max-w-[200px] object-contain"
                  />
                ) : (
                  <Image
                    src={p.logoSrc}
                    alt={p.name}
                    width={p.logoWidth}
                    height={p.logoHeight}
                    className="h-10 w-auto max-w-[200px] object-contain"
                  />
                )
              ) : (
                <span className="font-heading text-2xl font-bold text-primary">{p.name}</span>
              )}
              <p className="mt-4 text-center text-[10px] font-black uppercase tracking-[0.2em] text-gold">
                {p.role}
              </p>
            </Link>
          ))}
        </div>

        {!portwiseUrl && (
          <p className="mt-8 text-center text-xs text-muted-foreground">
            {copy.missingPartnerNotice}
          </p>
        )}
      </div>
    </section>
  );
}
